<template>
    <modal size="lg" @close="reset(); $emit('close')" :height-max="true" ref="modal" :is-closeable="isCloseable" :autoshow="true">
        <template v-slot:title>
            <h3 class="modal-title">Add Tax Registration</h3>
        </template>

        <slot>
            <!-- Step 1: Select company + state, then choose which taxes to register -->
            <div v-if="step == 'selection'">
                <div class="form-row mb-3">
                    <div class="col-6">
                        <form-input label="Company" v-model="formData.company_id" :errors="errors.company_id" type="select" :options="companyOptions" />
                    </div>
                    <div class="col-6">
                        <form-input label="State" v-model="filterState" :errors="errors.state" type="select" :options="stateOptions" />
                    </div>
                </div>

                <p v-if="!formData.company_id || !filterState">
                    <em>Select a company and a state to see available taxes.</em>
                </p>
                <p v-else-if="symTaxesLoaded && !allSymTaxes.length">
                    <em>No available taxes were found for this state.</em>
                </p>

                <fast-table
                    v-if="formData.company_id && filterState && symTaxesLoaded"
                    :sections="tableSections"
                    :headers="tableHeaders"
                    :data="tableData"
                    @select="onSelectTaxes"
                    @click="onRowClick"
                    :has-row-select="true"
                    :show-search="true"
                    :show-filter="false"
                    :show-more-actions="false"
                    :has-fixed-sort-order="true"
                />
            </div>

            <!-- Step 2: Fill in registration details for selected taxes -->
            <div v-if="step == 'registration'">
                <p class="text-right mt-1" v-if="selectedSymTaxes.length">
                    <button class="btn btn-link" @click.prevent="setToEIN()">Use Company EIN</button>
                </p>

                <p v-if="!selectedSymTaxes.length"><em>No taxes selected.</em></p>

                <div class="card mb-2" v-for="tax in selectedSymTaxes" :key="tax.unique_tax_id">
                    <div class="card-header pr-1 d-flex justify-content-between">
                        <h4 class="pt-1">{{ tax.description }}</h4>
                        <more-info class="my-0">
                            <slot name="popover-helper">
                                Symmetry Tax ID: {{ tax.unique_tax_id }}
                            </slot>
                        </more-info>
                    </div>


                    <div class="card-body py-1">
                        <tax-registration-form
                            ref="companyTaxRegistrationForms"
                            :unique-tax-id="tax.unique_tax_id"
                            :has-rate="tax.needs_rate"
                            :usesEIN="false"
                        />
                    </div>
                </div>
            </div>
        </slot>

        <template v-slot:footer>
            <form-errors :errors="formErrors"/>

            <button-row stacked>
                <button v-if="step == 'registration'" type="button" class="btn btn-outline-primary" @click.prevent="step = 'selection'">
                    Back
                </button>
                <button v-if="step == 'selection'" type="button" class="btn btn-outline-primary" @click.prevent="close()">
                    Cancel
                </button>
                <button v-if="step == 'selection'" type="button" class="btn btn-primary" @click.prevent="onNext()">
                    Next
                </button>
                <button v-if="step == 'registration'" type="button" class="btn btn-primary" @click.prevent="onSubmit()">
                    Save
                </button>
            </button-row>
        </template>
    </modal>
</template>

<script>
import FormMixin from '@/mixins/Form'
import ModalMixin from '@/mixins/ModalMixin'
import FastTable from '@/components/FastTable'
import TaxRegistrationForm from './TaxRegistrationForm'
import STATES from '@/states'

export default {
    props: ['client', 'taxRegistrations', ],
    mixins: [FormMixin, ModalMixin],
    components: {FastTable, TaxRegistrationForm, },
    data() {
        return {
            formData: this.makeFormData(),
            step: 'selection',
            filterState: '',
            allSymTaxes: [],
            symTaxesLoaded: false,
            selectedUniqueIds: [],
            tableHeaders: [
                {label: 'Tax', classes: '', defaultSort: false, isFilterable: false, isSearchable: true},
                {label: 'Type', classes: 'cw-12', defaultSort: false, isFilterable: true, isSearchable: false},
            ],
        }
    },
    computed: {
        selectedCompany() {
            return this.client.companies.find(c => c.id == this.formData.company_id)
        },
        companyOptions() {
            const result = this.client.companies.map(c => ({text: c.name, value: c.id}))
            result.sort((a, b) => a.text.localeCompare(b.text))
            return result
        },
        stateOptions() {
            return STATES.map(s => ({text: s.name, value: s.abbreviation}))
        },
        alreadyRegisteredUniqueIds() {
            if (!this.formData.company_id) { return [] }
            return this.taxRegistrations
                .filter(tr => tr.company_id == this.formData.company_id)
                .map(tr => tr.sym_tax.unique_tax_id)
        },
        tableSections() {
            return [
                {title: 'State Level', id: 'state'},
                {title: 'Local/Municipal', id: 'local'},
            ]
        },
        tableData() {
            const makeRow = tax => ({
                id: tax.unique_tax_id,
                isSelected: this.selectedUniqueIds.includes(tax.unique_tax_id) || this.alreadyRegisteredUniqueIds.includes(tax.unique_tax_id),
                selectDisabled: this.alreadyRegisteredUniqueIds.includes(tax.unique_tax_id),
                disableHover: this.alreadyRegisteredUniqueIds.includes(tax.unique_tax_id),
                object: tax,
                cells: [
                    {
                        value: tax.description,
                        _meta: {
                            additionalCSSClasses: this.alreadyRegisteredUniqueIds.includes(tax.unique_tax_id) ? 'text-muted' : '',
                        },
                    },
                    {
                        value: tax.tax_type_pretty,
                        _meta: {
                            additionalCSSClasses: this.alreadyRegisteredUniqueIds.includes(tax.unique_tax_id) ? 'text-muted' : '',
                        },
                    },
                ],
            })

            const stateTaxes = this.allSymTaxes.filter(t => this.isStateLevelTax(t))
            const localTaxes = this.allSymTaxes.filter(t => !this.isStateLevelTax(t))

            stateTaxes.sort((a, b) => a.sort_priority > b.sort_priority ? 1 : -1)
            localTaxes.sort((a, b) => a.sort_priority > b.sort_priority ? 1 : -1)

            return [stateTaxes.map(makeRow), localTaxes.map(makeRow)]
        },
        selectedSymTaxes() {
            const result = Array.from(this.allSymTaxes.filter(t => this.selectedUniqueIds.includes(t.unique_tax_id)))

            result.sort((a, b) => a.sort_priority > b.sort_priority ? 1 : -1)
            return result
        },
    },
    watch: {
        'formData.company_id'() {
            this.onSelectionChange()
        },
        filterState() {
            this.onSelectionChange()
        },
    },
    methods: {
        makeFormData() {
            return {company_id: ''}
        },
        isStateLevelTax(tax) {
            return Boolean(tax.state_code) &&
                !tax.city_code &&
                !tax.county_code &&
                !tax.municipality_code &&
                !tax.school_code &&
                !tax.transit_district_code &&
                !tax.psd_code
        },
        onSelectionChange() {
            this.allSymTaxes = []
            this.symTaxesLoaded = false
            this.selectedUniqueIds = []

            if (this.formData.company_id && this.filterState) {
                this.loadSymTaxes()
            }
        },
        loadSymTaxes() {
            this.$store.dispatch('START_LOADING')
            this.$api.get(`/sym-taxes?state=${this.filterState}`).then(resp => {
                this.allSymTaxes = resp
                this.symTaxesLoaded = true
                this.$store.dispatch('STOP_LOADING')
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__)
            })
        },
        onSelectTaxes(selectedRows) {
            this.selectedUniqueIds = Array.from(selectedRows.filter(r => !r.selectDisabled).map(r => r.object.unique_tax_id))
        },
        onRowClick(row) {
            if (this.selectedUniqueIds.includes(row.unique_tax_id)) {
                this.selectedUniqueIds = this.selectedUniqueIds.filter(x => x != row.unique_tax_id)
            }
            else {
                this.selectedUniqueIds.push(row.unique_tax_id)
            }
        },
        getFormData() {
            const result = {}
            this.$refs.companyTaxRegistrationForms.forEach(f => {
                result[f.uniqueTaxId] = f.getFormData()
            })
            return result
        },
        onNext() {
            this.errors = {}
            this.formErrors = []
            let isValid = true

            if (!this.formData.company_id) {
                this.errors.company_id = ['Please select a company.']
                isValid = false
            }
            if (!this.filterState) {
                this.errors.state = ['Please select a state.']
                isValid = false
            }
            if (!this.selectedUniqueIds.length) {
                this.formErrors = ['Please select at least one tax to register.']
                isValid = false
            }
            if (!isValid) { return }

            this.step = 'registration'
        },
        onSubmit() {
            let isValid = true
            this.$refs.companyTaxRegistrationForms.forEach(f => {
                isValid = f.validate() && isValid
            })
            if (!isValid) { return }

            this.$store.dispatch('START_LOADING')
            const url = `/clients/${this.client.id}/settings/companies/${this.formData.company_id}/tax-registrations`
            this.$api.post(url, {taxes: this.getFormData()}).then(() => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showInfo(`Successfully added ${this.selectedUniqueIds.length} tax registration(s).`)
                this.$emit('updated')
                this.close()
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                this.$refs.companyTaxRegistrationForms.forEach(f => {
                    if (errors.hasOwnProperty(f.uniqueTaxId)) {
                        f.setErrors(errors[f.uniqueTaxId])
                    }
                })
                this.formErrors = (errors.__all__ || [])
                this.$bus.showError('Please check the form for any missing fields.')
            })
        },
        setToEIN() {
            this.$refs.companyTaxRegistrationForms.forEach(f => {
                f.setFilingID(this.selectedCompany.EIN)
            })
        },
    }
}
</script>
